import { useAuth0 } from '@auth0/auth0-react';
import React, { useContext, useState } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import TransformFromPascalCase from 'components/Settings/Utilities/TransformFromPascalCase';
import transformToPascalCase from 'components/Settings/Utilities/TransformToPascalCase';
import ItemNew from 'components/Dashboard/KeyUpdates/ItemNew';
import ParliamentaryItem from 'components/Dashboard/ParliamentaryRecord/ParlamentaryItem';
import TweetItem from 'components/Dashboard/SocialMedia/TweetItem';
import normalize from 'components/Dashboard/utilities/normalizeString';
import useIsMobile from 'components/Dashboard/utilities/useIsMobile';
import {
  searchIncludesTitleConditional,
  relevantActivityFalseCondition,
} from 'components/Dashboard/StakeHolders/searchIncludesTitle';
import transformGroup from 'components/Dashboard/StakeHolders/TransformGroup';
import StakeholderTopicPicker from './StakeholderTopicPicker';
import { trimLastCharacter } from 'components/Dashboard/helpers/trimLastCharacter';
import { useSiteSectionsNames } from 'utils/getAppSite';
import ActivityTabs from 'components/Dashboard/StakeHolders/StakeholderPage/ui/ActivitySections/ActivityTabs';
import StakeholderMentionsOverTime from 'components/Dashboard/StakeHolders/StakeholderPage/ui/ActivitySections/StakeholderMentionsOverTime';
import StakeholderSentimentSection from 'components/Dashboard/StakeHolders/StakeholderPage/ui/ActivitySections/StakeholderSentimentSection';
import StakeholderAISummary from 'components/Dashboard/StakeHolders/StakeholderPage/ui/ActivitySections/StakeholderAISummary';
import {
  getContributionsText,
  compileNumberOfContributions,
} from 'components/Dashboard/StakeHolders/createStakeholderNumbers';
import useStakeholderSentimentSnapshotApiCalls from 'components/Dashboard/StakeHolders/StakeholderPage/ui/ActivitySections/useStakeholderSentimentSnapshotApiCalls';
import useStakeholderAISummaryTab from 'components/Dashboard/StakeHolders/StakeholderPage/ui/ActivitySections/useStakeholderAISummaryTab';
import { store } from 'components/Store';
import { ActivitySkeleton } from './StakeholderPageSkeleton';

const ActivityElements = React.forwardRef((props, ref) => {
  const [activeTab, setActiveTab] = useState('Activity');
  const {
    stakeholderSearchCondition,
    StakeholderInformation,
    contentRelated,
    isLoadingActivity,
    contentScrolled,
    searchPageQuery,
    localTopic,
    setLocalTopic,
    setClickOnSeeMore,
    scrollBarRef,
    keywordsLists,
    seeAllActivity,
    setShowPopup,
    onTopicPickerStateChange,
    isRightSideLoading,
  } = props;

  const topicPickerProps = {
    localTopic,
    setLocalTopic,
    searchPageQuery,
    keywordsLists,
    seeAllActivity,
    StakeholderInformation,
    setShowPopup,
    stakeholderSearchCondition,
    activeTabState: { activeTab, setActiveTab },
    onDropdownStateChange: onTopicPickerStateChange,
    disabled: false,
  };
  const { name } = StakeholderInformation;

  const noResultsConditional = () => {
    let results = [];
    for (let i in contentRelated) {
      let section = contentRelated[i];
      results.push(section?.hits);
    }
    return results.filter((item) => !!item).flat().length === 0;
  };

  const isMobile = useIsMobile();

  const location = useLocation();
  const params = useParams();
  const { id, contentType } = params;
  const { data, isLoadingSentimentSnapshot, noSearchCondition, sentimentSnapshotAnalyticsState } =
    useStakeholderSentimentSnapshotApiCalls({
      StakeholderInformation,
    });
  const {
    generateSummary,
    generatingSummary,
    isLoadingStakeholderAISummary,
    content,
    currentlyProcessing,
    summaryFinishes,
  } = useStakeholderAISummaryTab({
    StakeholderInformation,
  });

  return (
    <>
      <div className='d-flex flex-column' ref={ref} style={{ minHeight: `100%` }}>
        <div
          className={`${isMobile ? '' : 'sticky-item'} activity-title-container ${
            contentScrolled && !contentType ? 'activity-title-scrolled' : ''
          }`}
        >
          <div className='activity-title-element'>
            <div className='justify-content-between row mx-0 align-items-center py-3 px-4 py-lg-4 px-lg-5 border-bottom'>
              {!isMobile && (
                <Link
                  to={`${
                    contentType === undefined
                      ? location.pathname
                      : `/stakeholders/${id}/${normalize(name)}${location.search}`
                  }`}
                  onClick={(e) => {
                    if (contentType === undefined) {
                      e.preventDefault();
                    }
                  }}
                  className={`stake-main-title ${contentType === undefined ? '' : 'stake-main-title-content-related'}`}
                >
                  <h3 className='mb-0 title-h4-bold'>
                    {localTopic !== null || searchPageQuery ? 'Relevant activity' : 'Political activity'}
                  </h3>
                </Link>
              )}

              <div className='col-lg-10 px-0'>
                <StakeholderTopicPicker {...topicPickerProps} />
              </div>
            </div>
            {!contentType && (
              <ActivityTabs
                setShowPopup={setShowPopup}
                data={data}
                activeTabState={{ activeTab, setActiveTab }}
                scrollBarRef={scrollBarRef}
                noSearchCondition={noSearchCondition}
                generatingSummary={generatingSummary}
                summaryFinishes={summaryFinishes}
                currentlyProcessing={currentlyProcessing}
              />
            )}
          </div>
        </div>
        <div
          className={`stakeholder-activity-content-container flex-grow-1 border-right border-left bg-white d-flex flex-column`}
        >
          {isRightSideLoading && (
            <div className='px-4 px-lg-5'>
              <ActivitySkeleton />
            </div>
          )}
          {activeTab === 'Activity' && (
            <ActivityContent
              stakeholderSearchCondition={stakeholderSearchCondition}
              StakeholderInformation={StakeholderInformation}
              contentRelated={contentRelated}
              isLoadingActivity={isLoadingActivity}
              searchPageQuery={searchPageQuery}
              localTopic={localTopic}
              setClickOnSeeMore={setClickOnSeeMore}
              scrollBarRef={scrollBarRef}
              noResultsConditional={noResultsConditional}
              isRightSideLoading={isRightSideLoading}
            />
          )}
          {activeTab === 'Mentions' && (
            <StakeholderMentionsOverTime StakeholderInformation={StakeholderInformation} scrollBarRef={scrollBarRef} />
          )}
          {activeTab === 'Sentiment' && (
            <StakeholderSentimentSection
              sentimentSnapshotState={{
                isLoading: isLoadingSentimentSnapshot,
                data,
                noSearchCondition,
                sentimentSnapshotAnalyticsState,
              }}
              StakeholderInformation={StakeholderInformation}
              scrollBarRef={scrollBarRef}
            />
          )}
          {activeTab === 'AI summary' && (
            <StakeholderAISummary
              generateSummary={generateSummary}
              generatingSummary={generatingSummary}
              currentlyProcessing={currentlyProcessing}
              isLoadingStakeholderAISummary={isLoadingStakeholderAISummary}
              content={content}
              StakeholderInformation={StakeholderInformation}
              scrollBarRef={scrollBarRef}
            />
          )}
        </div>
      </div>
    </>
  );
});

const ActivityContent = (props) => {
  const {
    stakeholderSearchCondition,
    StakeholderInformation,
    contentRelated,
    isRightSideLoading,
    searchPageQuery,
    localTopic,
    setClickOnSeeMore,
    scrollBarRef,
    noResultsConditional,
  } = props;

  const globalState = useContext(store);
  const { state } = globalState;
  const { activePlan } = state;
  const { enableTwitter } = activePlan;
  const showTweets = import.meta.env.VITE_APP_ENABLE_TWITTER === 'true';
  const location = useLocation();
  const params = useParams();
  const { id, contentType } = params;
  const { getName } = useSiteSectionsNames();

  const { matchInformation, officalName, name, categories } = StakeholderInformation;
  const searchCondition = (localTopic !== null && localTopic.id !== null) || searchPageQuery !== '';
  const positionsCondition =
    searchCondition &&
    stakeholderSearchCondition &&
    contentType === undefined &&
    !searchIncludesTitleConditional(officalName) &&
    !relevantActivityFalseCondition();
  const positionsConditional =
    matchInformation?.groupMemberships?.length > 0 || matchInformation?.positions?.length > 0;

  const activeSearchResultsConditional =
    ((localTopic !== null && localTopic.id !== null) || searchPageQuery !== '') && stakeholderSearchCondition;

  const individualItemProperties = {
    setClickOnSeeMore,
    contentRelated,
    contentType,
    stakeholderUrl: `/stakeholders/${id}/${normalize(name)}${location.search}`,
    scrollBarRef,
  };

  const getEmptyMessage = () => {
    const ppcCpndition =
      categories?.includes('PPC 2024/25') &&
      !categories?.includes('MP') &&
      !categories?.includes('Lord') &&
      !categories?.includes('Special Adviser') &&
      !categories?.includes('MSP') &&
      !categories?.includes('MS');
    if (ppcCpndition) {
      return 'Parliamentary activity is not available for candidates';
    } else if (categories?.includes('Special Adviser')) {
      return 'Parliamentary activity is not available for Special Advisers';
    } else {
      return 'No activity to show';
    }
  };

  const shouldShowNoActivityMessage = noResultsConditional();

  const shouldShowPositions = positionsCondition && positionsConditional;

  if (isRightSideLoading) {
    return null;
  }

  return (
    <>
      {shouldShowNoActivityMessage ? (
        <>
          {contentType && (
            <h4 className='category-subtitle mt-4 px-4 px-lg-5'>
              {TransformFromPascalCase(transformToPascalCase(contentType))}
            </h4>
          )}

          <div className='no-activity-message text-center activity'>
            <p className='mb-2 light-70-text'>{getEmptyMessage()}</p>
          </div>
        </>
      ) : (
        <>
          {shouldShowPositions && (
            <div className='px-4 px-lg-5 pt-2'>
              <h3 className='category-subtitle mb-2 line-height-1-5'>Positions and committee memberships</h3>
              <div className='parliamentarian-positions-container px-3 py-2 mb-4'>
                <ul className='px-0 policy-list mb-0 stake-list-information'>
                  {matchInformation.positions.length > 0 && (
                    <>
                      {matchInformation.positions.map((item, index) => (
                        <li key={`item${index}`}>
                          <span className={`icon-star`} style={{ fontSize: '11px' }} /> {item}
                        </li>
                      ))}
                    </>
                  )}
                  {matchInformation.groupMemberships.length > 0 && (
                    <>
                      {matchInformation.groupMemberships.map((item, index) => (
                        <li key={`item-group${index}`}>
                          <span className={`icon-group`} style={{ fontSize: '8px' }} /> Member of{' '}
                          {`${item.name} ${transformGroup(item.type)}`}
                        </li>
                      ))}
                    </>
                  )}
                </ul>
              </div>
            </div>
          )}

          <div className={`px-4 px-lg-5 pb-1 ${positionsCondition && positionsConditional ? '' : 'pt-2'}`}>
            {!activeSearchResultsConditional && (
              <IndividualActivityItems {...individualItemProperties} type='KeyUpdate' title={'Key updates'} />
            )}

            <IndividualActivityItems
              {...individualItemProperties}
              type='ParliamentaryRecord'
              title={`${getName({ property: 'Spoken parliamentary contributions' })}`}
            />
            <IndividualActivityItems
              {...individualItemProperties}
              type='WrittenAnswer'
              title={`Answers to written parliamentary questions`}
            />
            <IndividualActivityItems
              {...individualItemProperties}
              type='WrittenQuestion'
              title={`Written parliamentary contributions`}
            />
            <IndividualActivityItems
              {...individualItemProperties}
              type='EdmPrimaryOrSupported'
              title={`Early Day Motions as primary sponsor or supporter`}
            />
            {enableTwitter && showTweets && (
              <IndividualActivityItems {...individualItemProperties} customUrl='tweet' type='tweet' title={'X posts'} />
            )}
            {activeSearchResultsConditional && (
              <IndividualActivityItems {...individualItemProperties} type='KeyUpdate' title={'Key updates'} />
            )}
            {!enableTwitter &&
              showTweets &&
              compileNumberOfContributions(StakeholderInformation?.ranking ?? {}, ['tweet', 'retweet']) > 0 && (
                <DisabledTwitterMessage ranking={StakeholderInformation?.ranking} />
              )}
          </div>
        </>
      )}
    </>
  );
};

const IndividualActivityItems = (props) => {
  const { contentRelated, type, title, contentType, setClickOnSeeMore, scrollBarRef, customUrl } = props;
  const section = contentRelated[type];
  const items = section?.hits ?? [];
  const location = useLocation();
  const isMobile = useIsMobile();
  const { isAuthenticated } = useAuth0();

  return (
    <>
      {items.length > 0 && (
        <div className='mb-4'>
          <h3
            className={`category-subtitle d-block mb-2 stake-main-title line-height-1-5 ${
              contentType && isMobile ? 'stake-main-title-content-related' : ''
            }`}
          >
            {title}
          </h3>
          <ul className='policy-list items-list px-0'>
            {items.map((item, index) => {
              const rowKey = `${normalize(type)}-${item.objectID}`;
              const baseListRow = {
                listContext: {
                  index,
                  item,
                  scrollBarRef,
                  nameSection: 'Stakeholders',
                },
                feedOverrides: {
                  explanations: section?.explanations ?? [],
                  feedItemFlags: section?.feedItemFlags ?? [],
                },
                sectionMeta: { useContentRelatedForFeedback: true },
              };
              switch (type) {
                case 'ParliamentaryRecord':
                case 'WrittenQuestion':
                case 'WrittenAnswer':
                case 'EdmPrimaryOrSupported':
                case 'HolyroodCountrySpecificAnswer':
                case 'SeneddCountrySpecificAnswer':
                  return (
                    <ParliamentaryItem
                      key={rowKey}
                      listRow={{
                        ...baseListRow,
                        sectionMeta: {
                          ...(baseListRow.sectionMeta ?? {}),
                          collapsedHits: item?.collapsedHits?.hits,
                          numberOfContributions: item?.collapsedHits?.totalHits,
                        },
                      }}
                    />
                  );
                case 'KeyUpdate':
                  return <ItemNew key={rowKey} listRow={baseListRow} />;
                case 'tweet':
                  return <TweetItem key={rowKey} listRow={baseListRow} />;
                default:
                  return null;
              }
            })}
          </ul>
          {section.thereIsMoreContent && contentType === undefined && (
            <>
              {isAuthenticated ? (
                <Link
                  className='see-more-link see-more-category'
                  to={`${trimLastCharacter(location.pathname, '/')}/${customUrl ?? normalize(title)}${location.search}`}
                  onClick={() => {
                    if (scrollBarRef && scrollBarRef.current) {
                      scrollBarRef.current.scrollTop();
                    }
                    setClickOnSeeMore(true);
                  }}
                >
                  <span className='px-2'>See more</span>
                </Link>
              ) : (
                <Link to={`/register?reg_source=use-stakeholder-more-button`} className='general-button light-50-text'>
                  For all results {isMobile ? '' : 'and filters'}, create an account
                </Link>
              )}
            </>
          )}
        </div>
      )}
    </>
  );
};

const DisabledTwitterMessage = (props) => {
  const { ranking } = props ?? {};
  const isMobile = useIsMobile();
  return (
    <div className='mb-4'>
      <h3
        className={`category-subtitle d-block mb-2 stake-main-title line-height-1-5 ${
          isMobile ? 'stake-main-title-content-related' : ''
        }`}
      >
        X posts
      </h3>
      <div className='rounded border'>
        <div className='px-4 bg-blue-hovered py-5 d-flex flex-column align-items-center'>
          <h4 className='mb-2 title-h5 font-weight-bold text-main-blue'>
            {getContributionsText(ranking ?? {}, ['tweet', 'retweet'], 'X post')}
          </h4>
          <div className='text-center'>
            <p className='mb-2 light-70-text stakeholder-page-disabled-twitter-description'>
              PolicyMogul’s X monitoring features let you see what policymakers are saying publicly, in real time.
              Available as an add-on.{' '}
              <span className={`pointer link-button contact-button-launch-messenger text-underline`}>Contact us</span>{' '}
              to enable X monitoring.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ActivityElements;
